import {confirmSkip} from '../../_helpers/skip-confirmation';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';

import {ActivatedRoute, Router} from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {
  getError,
  getResetPassword,
  getPasswordContext,
  getLoggedInUser,
  IAuthenticationState,
  LoginUser,
  ResetPassword,
  ResetAuthState,
  LogoutUser, ResetPasswordToken
} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import {Subject} from 'rxjs';
import {filter, take, takeUntil} from 'rxjs/operators';
import {UserAbilityService} from '@app/services/user-ability.service';
import {AuthenticationService, PendingSignIn} from '@app/services/authentication.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {mergeAuthTranslations} from '../auth-i18n';
import { PasswordValidationComponent } from '../../_components/password-validation/password-validation.component';
import { AuthMarketingPanelComponent } from '../auth-marketing-panel/auth-marketing-panel.component';

@Component({
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, PasswordValidationComponent, AuthMarketingPanelComponent, TranslateModule]
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastrService = inject(ToastrService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private userAbilityService = inject(UserAbilityService);
  private authenticationService = inject(AuthenticationService);
  private translate = inject(TranslateService);
  private sbModal = inject(SbModalService);

  resetPasswordForm: FormGroup<{password: FormControl<string | null>; confirm_password: FormControl<string | null>}>;
  returnUrl: string;
  submitted = false;
  token: string;
  unsubscriber = new Subject();
  confirmPasswordError: string;
  passwordStrength: any = '';
  passwordContext: any = {};
  passwordChangeReason = '';
  // The server says whether this particular reset may be put off once for a week; only
  // a reset forced by the rules tightening qualifies.
  deferrable = false;
  readonly deferring = signal(false);
  deferralsLeft = 0;
  deferralDays = 7;
  // True from the moment the new password is sent until the store answers - with
  // the reset done or with an error. The button is disabled meanwhile, so one
  // click is one request.
  readonly savePending = signal(false);
  // "Remember me" from the sign-in this reset interrupted; a reset from an email link has none.
  private remember = false;
  private submittedCredentials: PendingSignIn | null = null;
  showPasswords: any = {
    password: false,
    confirm_password: false,
  };
  timer = null;

  constructor() {
    this.authenticationStore.dispatch(ResetAuthState({params: {error: ''}}));
    // localStorage.removeItem('resetUser');
    if (localStorage.getItem('resetUser') === null && !this.route.snapshot.queryParams['token']) {
      this.router.navigate(['/login']);
    }
    this.token = this.route.snapshot.queryParams['token'];
    this.passwordChangeReason = this.route.snapshot.queryParams['reason'] || '';
    this.deferrable = this.route.snapshot.queryParams['deferrable'] === '1';
    this.deferralsLeft = Number(this.route.snapshot.queryParams['deferrals_left'] || 0);
    this.deferralDays = Number(this.route.snapshot.queryParams['deferral_days'] || 7);
    this.remember = this.route.snapshot.queryParams['remember'] === '1';
    this.subscribeStores();
  }

  subscribeStores() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user && user.isPasswordReadyForLogin() && localStorage.getItem('resetUser') === null && localStorage.getItem('currentUser')) {
          setTimeout(() => {
            this.router.navigate([this.userAbilityService.getDefaultDashboard(user)]);
          });
        }
      });

    this.authenticationStore.pipe(select(getResetPassword))
      .pipe(
        filter(resetPassword => resetPassword?.success),
        take(1),
        takeUntil(this.unsubscriber)
      )
      .subscribe(() => {
        this.savePending.set(false);
        this.authenticationStore.dispatch(ResetAuthState({
          params: {resetPassword: null, authFlow: null}
        }));
        // Sign straight in with the new password rather than asking for it again. Without
        // an address to sign in as, fall back to the sign-in form.
        if (this.submittedCredentials) {
          this.authenticationService.handOffSignIn(this.submittedCredentials);
          this.submittedCredentials = null;
          this.toastrService.success('Password changed. Signing you in…');
        } else {
          this.toastrService.success('Password reset successfully. Please sign in.');
        }
        this.router.navigate(['/login'], {replaceUrl: true});
      });

    this.authenticationStore.pipe(select(getPasswordContext))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((passwordContext) => {
        this.passwordContext = passwordContext || {};
        this.passwordChangeReason = passwordContext?.reset_reason
          || this.passwordChangeReason;
        // The validated token is the authority; the query flag only pre-renders.
        if (passwordContext && 'deferrable' in passwordContext) {
          this.deferrable = !!passwordContext.deferrable;
        }
        if (passwordContext?.deferrals_left !== undefined) {
          this.deferralsLeft = Number(passwordContext.deferrals_left) || 0;
        }
        if (passwordContext?.deferral_days) {
          this.deferralDays = Number(passwordContext.deferral_days) || 7;
        }
        // The context fills the message, the strength checks and the skip offer.
        this.cdr.markForCheck();
      });

    this.authenticationStore.pipe(select(getError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.savePending.set(false);
          this.toastrService.error(error);
        }
      });
  }

  ngOnInit() {
    mergeAuthTranslations(this.translate, this.unsubscriber);

    this.resetPasswordForm = this.formBuilder.group({
      password: ['', Validators.required],
      confirm_password: ['', Validators.required]
    },
    {validators: [this.passwordMatchValidator, this.setNotMatchValidation.bind(this)]}
    );
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    // this.token = this.route.snapshot.queryParams['token'];
    if (!!this.token) {
      this.tokenLogin();
    }
  }

  tokenLogin() {
    // this.authenticationStore.dispatch(LogoutUser());
    this.authenticationStore.dispatch(ResetPasswordToken({token: this.token}));
  }

  get canSkipForWeek(): boolean {
    // Expiry is postponable on the same terms as a rule tightening. The server decides
    // with `deferrable`; this only checks the reason is one the offer applies to.
    return this.deferrable && !!this.token
      && ['policy_upgrade', 'expired'].includes(this.passwordChangeReason);
  }

  /**
   * Put the change off once. The reset token is spent by the server in exchange for a
   * week in which the current password keeps working, so the user goes back to sign in.
   */
  async skipForWeek(): Promise<void> {
    if (!this.canSkipForWeek || this.deferring()) {
      return;
    }
    this.deferring.set(true);
    const agreed = await confirmSkip(this.sbModal, this.translate, 'password', {
      days: this.deferralDays,
      skipsLeft: this.deferralsLeft
    });
    if (!agreed) {
      this.deferring.set(false);
      return;
    }
    this.authenticationService.deferPasswordReset(this.token).subscribe({
      next: (response: any) => {
        const until = response?.deferred_until ? new Date(response.deferred_until) : null;
        this.toastrService.success(this.translate.instant('AuthSecurity.skip_reset_done', {
          date: until ? until.toLocaleDateString() : ''
        }));
        this.authenticationStore.dispatch(ResetAuthState({params: {resetPassword: null, authFlow: null}}));
        this.router.navigate(['/login'], {replaceUrl: true});
      },
      error: (error) => {
        this.deferring.set(false);
        this.deferrable = false;
        this.cdr.markForCheck();
        this.toastrService.error(error?.error?.message || 'This password change cannot be postponed.');
      }
    });
  }

  setNotMatchValidation(frm: FormGroup) {
    if (frm.controls['password'].errors && !frm.controls['password'].errors.passwordRegex) {
      return;
    }
    if (this.passwordStrength >= 100) {
      frm.controls['password'].setErrors(null);
    } else {
      frm.controls['password'].setErrors({passwordRegex: true});
    }
  }

  updatePasswordStrength(event) {
    this.passwordStrength = event;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.resetPasswordForm.get('password').updateValueAndValidity();
      // The password's errors are bound in the template.
      this.cdr.markForCheck();
    }, 200);
  }

  passwordMatchValidator(frm: FormGroup) {
    if (frm.controls['confirm_password'].errors && !frm.controls['confirm_password'].errors.confirmPasswordValidator) {
      return;
    }
    if (frm.controls['password'].value !== frm.controls['confirm_password'].value) {
      frm.controls['confirm_password'].setErrors({confirmPasswordValidator: true});
    } else {
      frm.controls['confirm_password'].setErrors(null);
    }
  }

  // Form Validation function.
  get f() {
    return this.resetPasswordForm.controls;
  }

  get passwordChangeMessage(): string {
    switch (this.passwordChangeReason) {
      case 'expired':
        return 'Your password has expired. Create a new password before continuing.';
      case 'policy_upgrade':
        return 'Your current password no longer meets the security policy. '
          + 'Create a new password that satisfies all requirements below.';
      case 'admin_forced':
        return 'An administrator requires you to change your password before continuing.';
      case 'activation':
        return 'Set a secure password to activate your Snöball account.';
      case 'requested':
        return 'You requested a password reset. Create a new password to continue.';
      default:
        return 'You must create a new password before continuing.';
    }
  }

  /**
   * Returns success object that will use for showing response messages .
   *
   * @param  url  an absolute URL giving the reset password page of the Application.
   * @param  name the location of the request, relative to the url.
   * @return      success object with user data.
   */

  onSubmit() {
    this.confirmPasswordError = '';
    this.submitted = true;
    // stop here if the form is invalid, or the previous attempt has not answered yet
    if (this.resetPasswordForm.invalid || this.savePending()) {
      return;
    }
    if (!this.token) {
      return;
    }
    this.savePending.set(true);
    // Taken now: the reset clears the password context from the store, address included.
    this.submittedCredentials = this.passwordContext?.email
      ? {email: this.passwordContext.email, password: this.f.password.value, remember: this.remember}
      : null;
    this.authenticationStore.dispatch(ResetPassword({password: this.f.password.value, token: this.token}));
  }

  /**
   * Leave for the sign-in page. The flow that sent the person here is still in the store,
   * and the sign-in page answers it by coming straight back, so it is cleared first.
   */
  backToSignIn(): void {
    this.authenticationStore.dispatch(ResetAuthState({
      params: {authFlow: null, resetPassword: null, passwordContext: null}
    }));
    localStorage.removeItem('resetUser');
    this.router.navigate(['/login'], {replaceUrl: true});
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
